import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { MARKETING_URL, PLATFORM_API_URL } from "../lib/api";
import {
  DEFAULT_LOCALE,
  LOCALES,
  immediateLocale,
  localeCookie,
  localeForCountry,
} from "./locale";

/*
 * The customer dashboard's arrangement, on a smaller surface: English is
 * bundled and every other language is a chunk fetched when first needed (see
 * `manualChunks` in vite.config.ts), so the first paint of the common case
 * needs nothing, and a language is added by adding its folder of JSON and
 * naming it in `languages.json`.
 */
const bundled = import.meta.glob<Record<string, unknown>>(`../locales/en/*.json`, {
  eager: true,
  import: "default",
});
const lazy = import.meta.glob<Record<string, unknown>>(
  ["../locales/*/*.json", "!../locales/en/*.json"],
  { import: "default" },
);

const namespaceOf = (path: string) => /\/([^/]+)\.json$/.exec(path)?.[1];

const english: Record<string, Record<string, unknown>> = {};
for (const [path, messages] of Object.entries(bundled)) {
  const namespace = namespaceOf(path);
  if (namespace) english[namespace] = messages;
}

/** A namespace nobody translated resolves to an empty bundle, so English answers for it. */
const backend = {
  type: "backend" as const,
  init() {},
  read(
    language: string,
    namespace: string,
    callback: (error: unknown, data?: Record<string, unknown>) => void,
  ) {
    const load = lazy[`../locales/${language}/${namespace}.json`];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (messages) => callback(null, messages),
      (error: unknown) => callback(error),
    );
  },
};

const browser = typeof window !== "undefined";

/* Synchronous for the bundled language, so a test that renders on import finds English in place. */
const ready = i18n
  .use(backend)
  .use(initReactI18next)
  .init({
    resources: { [DEFAULT_LOCALE]: english },
    partialBundledLanguages: true,
    lng: (browser && immediateLocale(window.location.search, document.cookie)) || DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: Object.keys(english),
    defaultNS: "common",
    initAsync: false,
    interpolation: { escapeValue: false },
  });

if (browser) {
  document.documentElement.lang = i18n.language;
  i18n.on("languageChanged", (locale) => {
    document.documentElement.lang = locale;
  });
}

const GEO_TIMEOUT_MS = 1500;

/**
 * Settles the language before the first render for a visitor nothing has told
 * us about: no `?lang=` on the link in, no remembered choice. The country is
 * asked of the control plane's public `/v1/geo` -- this dashboard's own API
 * is the content plane, which has no such route -- and only where a platform
 * origin is configured. Bounded, so a slow answer costs a moment of English
 * rather than a blank page; the browser's own language is the fallback
 * before that.
 */
export async function detectLocale(): Promise<void> {
  await ready;
  if (!browser || immediateLocale(window.location.search, document.cookie)) return;

  const fromBrowser = navigator.languages
    .map((tag) => tag.split("-")[0])
    .find((code) => code !== DEFAULT_LOCALE && LOCALES.includes(code!));

  if (PLATFORM_API_URL) {
    try {
      const response = await Promise.race([
        fetch(`${PLATFORM_API_URL}/v1/geo`, { credentials: "omit" }),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), GEO_TIMEOUT_MS)),
      ]);
      const geo = (await response.json()) as { country: string | null };
      const locale = localeForCountry(geo.country) ?? fromBrowser;
      if (locale) await i18n.changeLanguage(locale);
      return;
    } catch {
      /* fall through to the browser's own language */
    }
  }
  if (fromBrowser) await i18n.changeLanguage(fromBrowser);
}

/**
 * An explicit choice, remembered in the cookie the marketing site and the
 * customer dashboard read too, so the three agree. `null` hands the decision
 * back to detection.
 */
export async function chooseLocale(locale: string | null): Promise<void> {
  if (browser) {
    document.cookie = localeCookie(
      locale,
      window.location.hostname,
      MARKETING_URL ?? window.location.origin,
      window.location.protocol === "https:",
    );
  }
  if (locale) {
    await i18n.changeLanguage(locale);
  } else {
    await i18n.changeLanguage(DEFAULT_LOCALE);
    await detectLocale();
  }
}

export { i18n };
export { Trans, useTranslation } from "react-i18next";
